import { Platform } from 'react-native';
import TimeSettingsChecker from 'react-native-time-settings-checker';
import { formatDate } from 'secullum-react-native-ui';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AvisoDivergenciaFusoHorario from '../containers/AvisoDivergenciaFusoHorarioContainer';
import Funcionario from '../containers/FuncionarioContainer';

const CHAVE_MODIFICACAO_DATA_HORA = 'ModificacaoDataHora';
const CHAVE_AVISO_DIVERGENCIA_HORARIO = 'AvisoDivergenciaHorarioExibido';
const TOLERANCIA_DIFERENCA = 5 * 60 * 1000; // 5 min em ms
// 10 min de tolerância, referente ao limite de tolerância diária do Art. 58 da CLT.
const TOLERANCIA_DIFERENCA_ONLINE = 10 * 60 * 1000;

interface DadosModificacaoDataHora {
  dataHoraServidorUtc: string;
  usuarioModificouDataHoraDispositivo: boolean;
}

export async function salvarDataHoraServidorAsync(dataHoraServidorUtc: string) {
  // Sempre que buscar a data e hora do servidor, limpa a propriedade que informa
  // se o usuário modificou a data e hora do dispositivo, já que na próxima vez que
  // for verificar vai ter a data e hora do servidor atualizada.
  const dados: DadosModificacaoDataHora = {
    dataHoraServidorUtc,
    usuarioModificouDataHoraDispositivo: false
  };

  await AsyncStorage.setItem(
    CHAVE_MODIFICACAO_DATA_HORA,
    JSON.stringify(dados)
  );
}

export async function verificarDataHoraModificadaAsync(): Promise<boolean> {
  const jsonDados = await AsyncStorage.getItem(CHAVE_MODIFICACAO_DATA_HORA);

  if (jsonDados == null) {
    return false;
  }

  const dados: DadosModificacaoDataHora = JSON.parse(jsonDados);

  // Depois que o usuário tiver modificado a data e hora uma vez,
  // todos os registros deverão ser enviados como suspeitos até
  // que ele sincronize com o servidor novamente.
  if (dados.usuarioModificouDataHoraDispositivo) {
    return true;
  }

  const dataServidor = Date.parse(dados.dataHoraServidorUtc);
  const dataDispositivo = Date.now();

  // Verifica se a data do dispositivo é anterior à última data buscada do servidor,
  // considerando uma tolerância para evitar falsos positivos.
  if (dataServidor - dataDispositivo > TOLERANCIA_DIFERENCA) {
    dados.usuarioModificouDataHoraDispositivo = true;

    await AsyncStorage.setItem(
      CHAVE_MODIFICACAO_DATA_HORA,
      JSON.stringify(dados)
    );

    return true;
  }

  return false;
}

// Busca o último horário UTC do servidor salvo pelo header "X-Sec-DataHoraServidorUtc"
// (ver Api.request), e não o "horaServidor" retornado no corpo do GET de IncluirPonto:
// esse último já vem ajustado pelo fuso configurado do funcionário, então não serve
// como instante absoluto para comparar com o relógio do aparelho.
async function obterUltimaHoraServidorUtcAsync(): Promise<Date | null> {
  const jsonDados = await AsyncStorage.getItem(CHAVE_MODIFICACAO_DATA_HORA);

  if (jsonDados == null) {
    return null;
  }

  const dados: DadosModificacaoDataHora = JSON.parse(jsonDados);

  return new Date(dados.dataHoraServidorUtc);
}

function verificarDivergenciaHorarioOnline(horaServidorUtc: Date): boolean {
  return (
    Math.abs(horaServidorUtc.getTime() - Date.now()) >
    TOLERANCIA_DIFERENCA_ONLINE
  );
}

// Calcula o horário no fuso configurado da empresa a partir dos getters UTC,
// em vez de formatar direto o Date (o que usaria o fuso do próprio aparelho),
// já que os dois podem divergir mesmo após o aviso de fuso ter sido dispensado.
function formatarHorarioNoFusoEmpresa(
  horaServidorUtc: Date,
  fusoMinutosFuncionario: number
): string {
  const horaComFuso = new Date(
    horaServidorUtc.getTime() + fusoMinutosFuncionario * 60000
  );

  return [
    horaComFuso.getUTCHours(),
    horaComFuso.getUTCMinutes(),
    horaComFuso.getUTCSeconds()
  ]
    .map(valor => valor.toString().padStart(2, '0'))
    .join(':');
}

async function avisoDivergenciaHorarioExibidoAsync(): Promise<boolean> {
  const valor = await AsyncStorage.getItem(CHAVE_AVISO_DIVERGENCIA_HORARIO);

  return valor === 'true';
}

export async function marcarAvisoDivergenciaHorarioExibidoAsync(): Promise<void> {
  await AsyncStorage.setItem(CHAVE_AVISO_DIVERGENCIA_HORARIO, 'true');
}

async function obterDivergenciaHorarioOnlineAsync(
  fusoMinutosFuncionario: number
): Promise<{
  valorDispositivo: string;
  valorEmpresa: string;
} | null> {
  const horarioJaExibido = await avisoDivergenciaHorarioExibidoAsync();

  if (horarioJaExibido) {
    return null;
  }

  const horaServidorUtc = await obterUltimaHoraServidorUtcAsync();

  if (
    horaServidorUtc == null ||
    !verificarDivergenciaHorarioOnline(horaServidorUtc)
  ) {
    return null;
  }

  return {
    valorDispositivo: formatDate(new Date(), 'HH:mm:ss'),
    valorEmpresa: formatarHorarioNoFusoEmpresa(
      horaServidorUtc,
      fusoMinutosFuncionario
    )
  };
}

export async function verificarEAtivarDivergenciaHorarioAsync(): Promise<boolean> {
  const fusoMinutosFuncionario = Funcionario.state.dados.fusoMinutos;

  const divergencia = await obterDivergenciaHorarioOnlineAsync(
    fusoMinutosFuncionario
  );

  if (!divergencia) {
    return false;
  }

  AvisoDivergenciaFusoHorario.ativar(
    'horario',
    'online',
    divergencia.valorDispositivo,
    divergencia.valorEmpresa
  );

  return true;
}

export async function verificarDataHoraAutomaticasAsync() {
  try {
    // Sec-issues#11376: Atualmente somente Android disponibiliza a verificação de data e hora automática,
    // portanto, retornamos true para outros sistemas.
    if (Platform.OS !== 'android') {
      return true;
    }

    return await TimeSettingsChecker.getAutomaticDateTimeEnabledAsync();
  } catch (ex) {
    // Se não conseguiu identificar o uso da opção, segue o fluxo para retornar true
    // e não acusar que o usuário não está usando data e hora automáticas.
  }

  return true;
}
